import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin } from "lucide-react";
import { businessInfo } from "../data/mock";

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const services = [
    { id: "trademark-registration", name: "Trademark Registration" },
    { id: "copyright-registration", name: "Copyright Registration" },
    { id: "company-formation", name: "Company Formation" },
    { id: "trademark-search", name: "Trademark Search" },
    { id: "amazon-brand-registry", name: "Amazon Brand Registry" }
  ];

  return (
    <footer className="bg-[#0f1c3f] text-white">
      {/* Main Footer */}
      <div className="container-custom px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <div className="flex items-center space-x-2 mb-6">
              <div className="w-12 h-12 bg-gradient-secondary rounded-lg flex items-center justify-center">
                <span className="text-[#1e3a8a] font-bold text-2xl">TB</span>
              </div>
              <div className="flex flex-col">
                <span className="text-white font-bold text-xl leading-tight">
                  {businessInfo.name}
                </span>
                <span className="text-sm text-gray-400">USPTO Filing Experts</span>
              </div>
            </div>
            <p className="text-gray-300 mb-6 leading-relaxed">
              Your trusted partner in trademark registration and intellectual property protection. 
              We help businesses secure their brand identity with expert legal services.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-xl font-bold mb-6 text-[#d4af37]">Quick Links</h3>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/"
                  onClick={scrollToTop}
                  className="text-gray-300 hover:text-[#d4af37] transition-colors duration-300 flex items-center"
                >
                  <span className="mr-2">›</span> Home
                </Link>
              </li>
              <li>
                <button
                  onClick={() => {
                    scrollToTop();
                    setTimeout(() => {
                      document.getElementById("about")?.scrollIntoView({ behavior: "smooth" });
                    }, 100);
                  }}
                  className="text-gray-300 hover:text-[#d4af37] transition-colors duration-300 flex items-center"
                >
                  <span className="mr-2">›</span> About Us
                </button>
              </li>
              <li>
                <button
                  onClick={() => {
                    scrollToTop();
                    setTimeout(() => {
                      document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" });
                    }, 100);
                  }}
                  className="text-gray-300 hover:text-[#d4af37] transition-colors duration-300 flex items-center"
                >
                  <span className="mr-2">›</span> Pricing
                </button>
              </li>
              <li>
                <button
                  onClick={() => {
                    scrollToTop();
                    setTimeout(() => {
                      document.getElementById("faq")?.scrollIntoView({ behavior: "smooth" });
                    }, 100);
                  }}
                  className="text-gray-300 hover:text-[#d4af37] transition-colors duration-300 flex items-center"
                >
                  <span className="mr-2">›</span> FAQ
                </button>
              </li>
              <li>
                <Link
                  to="/privacy-policy"
                  onClick={scrollToTop}
                  className="text-gray-300 hover:text-[#d4af37] transition-colors duration-300 flex items-center"
                >
                  <span className="mr-2">›</span> Privacy Policy
                </Link>
              </li>
              <li>
                <Link
                  to="/terms-conditions"
                  onClick={scrollToTop}
                  className="text-gray-300 hover:text-[#d4af37] transition-colors duration-300 flex items-center"
                >
                  <span className="mr-2">›</span> Terms & Conditions
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-xl font-bold mb-6 text-[#d4af37]">Our Services</h3>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service.id}>
                  <Link
                    to={`/services/${service.id}`}
                    onClick={scrollToTop}
                    className="text-gray-300 hover:text-[#d4af37] transition-colors duration-300 flex items-center"
                  >
                    <span className="mr-2">›</span> {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-xl font-bold mb-6 text-[#d4af37]">Contact Us</h3>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-[#d4af37] mt-1 flex-shrink-0" />
                <span className="text-gray-300 leading-relaxed">
                  {businessInfo.address}
                </span>
              </li>
              <li className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-[#d4af37] flex-shrink-0" />
                <a
                  href={`tel:${businessInfo.phone}`}
                  className="text-gray-300 hover:text-[#d4af37] transition-colors duration-300"
                >
                  {businessInfo.phone}
                </a>
              </li>
              <li className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-[#d4af37] flex-shrink-0" />
                <a
                  href={`mailto:${businessInfo.email}`}
                  className="text-gray-300 hover:text-[#d4af37] transition-colors duration-300"
                >
                  {businessInfo.email}
                </a>
              </li>
            </ul>

            {/* Trust Badges */}
            <div className="mt-8 pt-6 border-t border-white/10">
              <div className="flex flex-col space-y-2">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-[#d4af37] rounded-full"></div>
                  <span className="text-sm text-gray-300">USPTO Registered</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-[#d4af37] rounded-full"></div>
                  <span className="text-sm text-gray-300">15,000+ Trademarks Filed</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-[#d4af37] rounded-full"></div>
                  <span className="text-sm text-gray-300">98% Success Rate</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="container-custom px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-sm text-center md:text-left">
              © {new Date().getFullYear()} {businessInfo.name}. All rights reserved.
            </p>
            <p className="text-gray-400 text-sm text-center md:text-right">
              Professional Trademark & Intellectual Property Services
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
